import { describeError, FormError, useApi } from '@classgo/app-kit';
import type { ImportEntity, ImportPlacementGaps, ImportPlacements } from '@classgo/shared';
import { Button, Input, Modal, Select, useToast } from '@classgo/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { academicApi, listQuery } from '../../academic/api';
import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';

type Gap = ImportPlacementGaps['groups'][number];
type Jornada = 'unica' | 'manana' | 'tarde' | 'nocturna';

const NEW = '__new__';
const JORNADAS: readonly Jornada[] = ['unica', 'manana', 'tarde', 'nocturna'];

const linesOf = (lines: readonly number[]): string =>
  lines.length <= 8 ? lines.join(', ') : `${lines.slice(0, 8).join(', ')}…`;

const gradeNameOf = (gap: Gap): string =>
  gap.grade ?? (gap.name.replace(/\s+[A-Za-z0-9]{1,2}$/, '').trim() || gap.name);

const nextPosition = (items: readonly { position: number }[]): number =>
  Math.min(50, items.reduce((max, item) => Math.max(max, item.position), 0) + 1);

/**
 * Creates the group a file names, with its grade and level when they are missing too: the
 * minimum a group needs (grade, sede, jornada) in the year the rows resolve to. Nothing about
 * the import changes here; the review runs again afterwards and finds the group.
 */
const CreateGroupDialog = ({
  gap,
  yearName,
  onClose,
  onCreated,
}: {
  gap: Gap;
  yearName: string;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const { session, sede } = useTenant();
  const levels = useQuery(listQuery(api, 'levels', { pageSize: '200' }));
  const grades = useQuery(listQuery(api, 'grades', { pageSize: '200' }));
  const [form, setForm] = useState({
    name: gap.name,
    gradeId: gap.gradeId ?? NEW,
    gradeName: gradeNameOf(gap),
    levelId: '',
    levelName: '',
    sedeId: sede.id,
    jornada: 'unica' as Jornada,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const levelItems = levels.data?.items ?? [];
  const gradeItems = grades.data?.items ?? [];
  const levelId = form.levelId || (levelItems[0]?.id ?? NEW);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let gradeId = form.gradeId;
      if (gradeId === NEW) {
        let parent = levelId;
        if (parent === NEW) {
          const level = await academicApi(api).create('levels', {
            name: form.levelName.trim(),
            position: nextPosition(levelItems),
          });
          parent = level.id;
        }
        const grade = await academicApi(api).create('grades', {
          levelId: parent,
          name: form.gradeName.trim(),
          position: nextPosition(gradeItems),
        });
        gradeId = grade.id;
      }
      await academicApi(api).create('groups', {
        schoolYearId: gap.schoolYearId,
        sedeId: form.sedeId,
        gradeId,
        name: form.name.trim(),
        jornada: form.jornada,
      });
      await queries.invalidateQueries({ queryKey: ['school'] });
      await queries.invalidateQueries({ queryKey: ['setup'] });
      toast.show({
        tone: 'success',
        title: t('imports.placement.created', { name: form.name.trim() }),
      });
      onCreated();
    } catch (thrown) {
      setError(describeError(t, thrown));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('imports.placement.createTitle', { name: gap.name })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="placement-group-form" loading={busy}>
            {t('imports.placement.submit')}
          </Button>
        </>
      }
    >
      <form
        id="placement-group-form"
        className="space-y-4"
        onSubmit={(event) => void submit(event)}
      >
        <FormError message={error} />
        <p className="text-sm text-gray-600">{t('imports.placement.inYear', { year: yearName })}</p>
        <Input
          label={t('imports.placement.field.name')}
          required
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <Select
          label={t('imports.placement.field.grade')}
          value={form.gradeId}
          onChange={(event) => setForm({ ...form, gradeId: event.target.value })}
          options={[
            ...gradeItems.map((grade) => ({ value: grade.id, label: grade.name })),
            { value: NEW, label: t('imports.placement.newGrade', { name: form.gradeName }) },
          ]}
        />
        {form.gradeId === NEW ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('imports.placement.field.gradeName')}
              required
              value={form.gradeName}
              onChange={(event) => setForm({ ...form, gradeName: event.target.value })}
            />
            <Select
              label={t('imports.placement.field.level')}
              value={levelId}
              onChange={(event) => setForm({ ...form, levelId: event.target.value })}
              options={[
                ...levelItems.map((level) => ({ value: level.id, label: level.name })),
                { value: NEW, label: t('imports.placement.newLevel') },
              ]}
            />
            {levelId === NEW ? (
              <Input
                label={t('imports.placement.field.levelName')}
                help={t('imports.placement.field.levelName.help')}
                required
                value={form.levelName}
                onChange={(event) => setForm({ ...form, levelName: event.target.value })}
              />
            ) : null}
          </div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('imports.placement.field.sede')}
            value={form.sedeId}
            onChange={(event) => setForm({ ...form, sedeId: event.target.value })}
            options={session.sedes.map((option) => ({ value: option.id, label: option.name }))}
          />
          <Select
            label={t('imports.placement.field.jornada')}
            value={form.jornada}
            onChange={(event) => setForm({ ...form, jornada: event.target.value as Jornada })}
            options={JORNADAS.map((value) => ({
              value,
              label: t(`groups.jornada.${value}`),
            }))}
          />
        </div>
      </form>
    </Modal>
  );
};

/**
 * The rows a file could not place, and what the school decides for each group it names: create
 * the group, assign the rows to an existing one, or (students) import them without a group to
 * enroll later. Also the year for rows without one when the school has no active year. Every
 * decision runs the review again; nothing is written until the import.
 */
export const PlacementPanel = ({
  entity,
  gaps,
  placements,
  busy,
  onChange,
  onCreated,
}: {
  entity: ImportEntity;
  gaps: ImportPlacementGaps;
  placements: ImportPlacements;
  busy: boolean;
  onChange: (next: ImportPlacements) => void;
  onCreated: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const years = useQuery(listQuery(api, 'years', { pageSize: '200' }));
  const yearId =
    gaps.groups[0]?.schoolYearId ??
    placements.schoolYearId ??
    years.data?.items.find((year) => year.status === 'active')?.id;
  const groups = useQuery({
    ...listQuery(api, 'groups', { schoolYearId: yearId, pageSize: '200' }),
    enabled: Boolean(yearId),
  });
  const [creating, setCreating] = useState<Gap | null>(null);
  const yearName = (id: string | undefined) =>
    years.data?.items.find((year) => year.id === id)?.name ?? '';
  const groupName = (id: string) => groups.data?.items.find((group) => group.id === id)?.name ?? '';
  const decide = (gap: Gap, value: string) => {
    if (value === '') return;
    if (value === 'create') {
      setCreating(gap);
      return;
    }
    onChange({
      ...placements,
      groups: [
        ...placements.groups.filter((decision) => decision.name !== gap.name),
        { name: gap.name, groupId: value === 'none' ? null : value },
      ],
    });
  };
  const withoutYear = () => {
    const { schoolYearId: _year, ...rest } = placements;
    onChange(rest);
  };
  const hasDecisions = placements.groups.length > 0 || Boolean(placements.schoolYearId);
  if (gaps.noActiveYear.length === 0 && gaps.groups.length === 0 && !hasDecisions) return null;

  return (
    <section
      aria-labelledby="placement-title"
      className="mt-4 rounded-card border border-gray-200 bg-warning-100 p-4"
    >
      <h3 id="placement-title" className="text-base font-semibold text-gray-900">
        {t('imports.placement.title')}
      </h3>
      <p className="mt-1 text-sm text-gray-700">{t('imports.placement.intro')}</p>
      {busy ? (
        <p role="status" className="mt-2 text-sm text-gray-600">
          {t('imports.placement.rerunning')}
        </p>
      ) : null}

      {gaps.noActiveYear.length > 0 ? (
        <div className="mt-3 rounded-control bg-white p-3">
          <p className="text-sm text-gray-900">
            {t('imports.placement.noYear', { lines: linesOf(gaps.noActiveYear) })}
          </p>
          <div className="mt-2 max-w-sm">
            <Select
              label={t('imports.placement.year')}
              value=""
              disabled={busy}
              onChange={(event) => {
                if (event.target.value) {
                  onChange({ ...placements, schoolYearId: event.target.value });
                }
              }}
              options={[
                { value: '', label: t('imports.placement.choose') },
                ...(years.data?.items ?? []).map((year) => ({
                  value: year.id,
                  label: `${year.name} (${t(`years.status.${year.status}`)})`,
                })),
              ]}
            />
          </div>
          <Link
            to="/anos"
            className="control-touch mt-1 inline-flex items-center text-sm text-brand-700 underline underline-offset-2 hover:decoration-2"
          >
            {t('imports.placement.activate')}
          </Link>
        </div>
      ) : null}

      {gaps.groups.length > 0 ? (
        <ul className="mt-3 space-y-3">
          {gaps.groups.map((gap) => (
            <li key={`${gap.schoolYearId}|${gap.name}`} className="rounded-control bg-white p-3">
              <p className="text-sm text-gray-900">
                <span className="font-semibold">{gap.name}</span>
                {gap.grade ? ` · ${t('imports.placement.grade', { grade: gap.grade })}` : ''}
                {' · '}
                {t('imports.placement.rows', { lines: linesOf(gap.lines) })}
              </p>
              <div className="mt-2 max-w-sm">
                <Select
                  label={t('imports.placement.what', { name: gap.name })}
                  value=""
                  disabled={busy}
                  onChange={(event) => decide(gap, event.target.value)}
                  options={[
                    { value: '', label: t('imports.placement.choose') },
                    { value: 'create', label: t('imports.placement.create', { name: gap.name }) },
                    ...(groups.data?.items ?? []).map((group) => ({
                      value: group.id,
                      label: t('imports.placement.assign', { name: group.name }),
                    })),
                    ...(entity === 'students'
                      ? [{ value: 'none', label: t('imports.placement.none') }]
                      : []),
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {hasDecisions ? (
        <div className="mt-3">
          <p className="text-sm font-medium text-gray-900">{t('imports.placement.decisions')}</p>
          <ul className="mt-1 space-y-1 text-sm text-gray-700">
            {placements.schoolYearId ? (
              <li className="flex flex-wrap items-center gap-2">
                {t('imports.placement.decision.year', { year: yearName(placements.schoolYearId) })}
                <Button size="sm" variant="ghost" disabled={busy} onClick={withoutYear}>
                  {t('imports.placement.undo')}
                </Button>
              </li>
            ) : null}
            {placements.groups.map((decision) => (
              <li key={decision.name} className="flex flex-wrap items-center gap-2">
                {decision.groupId
                  ? t('imports.placement.decision.assign', {
                      from: decision.name,
                      to: groupName(decision.groupId),
                    })
                  : t('imports.placement.decision.none', { from: decision.name })}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  aria-label={t('imports.placement.undoFor', { name: decision.name })}
                  onClick={() =>
                    onChange({
                      ...placements,
                      groups: placements.groups.filter((other) => other.name !== decision.name),
                    })
                  }
                >
                  {t('imports.placement.undo')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {creating ? (
        <CreateGroupDialog
          gap={creating}
          yearName={yearName(creating.schoolYearId)}
          onClose={() => setCreating(null)}
          onCreated={() => {
            setCreating(null);
            onCreated();
          }}
        />
      ) : null}
    </section>
  );
};
